import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import './EventHeroBanner.css';
const backgroundImage = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1773292589/vectors-12_loogm2.png';
const banner02 = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775127544/Suprajanan_Newly_Wed_Couples_cfkwbl.png';
const banner03 = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775127543/Nurturing_Kids_Birth_up_to_7_years_qtfsa0.png';
const banner05 = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775127543/Meditation_Yoga_iileyc.png';
const banner06 = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775127543/Life_after_Death_i9ctg1.png';
const banner07 = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775127543/Health_Care_vrnzxw.png';
const banner08 = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775127543/Garbha_Sanskar_Prenatal_Education_louuit.png';
const banner09 = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775127543/Child_Development_8_to_14_years_xgrpj1.png';
const banner10 = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775127544/Science_of_Spirituality_dskjwj.png';
const banner04 = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1775127544/Stress_Management_xxhnl1.png';
const banner11 = 'https://res.cloudinary.com/dtuy184dc/image/upload/v1777011527/Youth_Power_15_to_28_years.jpg_o1ok8a.jpg';
     
const getShortTopicLabel = (label = '') => {
  if (!label) return '';

  // Case 1: Garbha Sanskar (Prenatal Education)
  if (label.includes('(') && label.includes(')')) {
    return label.substring(
      label.lastIndexOf('(') + 1,
      label.lastIndexOf(')')
    ).trim();
  }

  // Case 2: Nurturing Kids: Birth up to 7 years
  if (label.includes(':')) {
    return label.split(':').pop().trim();
  }

  return label;
};

// Choose banner image by matching topic label keywords (case-insensitive).
// Order matters: more specific patterns must come before broader ones.
const getBannerForTopic = (topicLabel = '') => {
  const full = String(topicLabel || '').toLowerCase().trim();
  const short = getShortTopicLabel(topicLabel).toLowerCase().trim();
  const t = `${full} ${short}`;
  if (!t.trim()) return null;

  // Youth Power: 15 to 28 years — must precede child/8/14 check because "28" contains "8"
  if (t.includes('youth') || (t.includes('15') && t.includes('28'))) return banner11;

  // Newly Wed Couples
  if (t.includes('newly') || t.includes('newlywed')) return banner02;

  // Birth up to 7 years
  if (t.includes('birth') && (t.includes('upto') || t.includes('up to') || /\b7\b/.test(t))) return banner03;

  // Prenatal Education — check before child/8/14 to avoid overlap
  if (t.includes('prenat') || t.includes('garbh')) return banner08;

  // Child Development: 8 to 14 years — use word-bounded numbers to avoid matching "28"
  if (t.includes('child') || t.includes('8 to 14') || /\b8\b/.test(t) || /\b14\b/.test(t)) return banner09;

  // Stress Management
  if (t.includes('stress') && t.includes('manag')) return banner04;

  // Meditation and yoga
  if (t.includes('medit') || t.includes('yoga')) return banner05;

  // Life after death
  if (t.includes('life') && t.includes('death')) return banner06;

  // Health care
  if (t.includes('health') && t.includes('care')) return banner07;

  // Science and Spirituality
  if (t.includes('science') && t.includes('spirit')) return banner10;

  return null;
};

// Prefer selecting banner by numeric topic ID when available (matches EventScheduleCard mapping)
const getBannerForTopicId = (topicId) => {
  if (!topicId && topicId !== 0) return null;
  let id;
  if (typeof topicId === 'object') {
    // Handle various object shapes the topic might arrive as
    id = parseInt(topicId.id || topicId.topicId || topicId.topic_id, 10);
  } else {
    id = parseInt(topicId, 10);
  }
  if (isNaN(id)) return null;

  const mapping = {
    1: banner02, // Newly Wed Couples
    2: banner08, // Prenatal Education
    3: banner03, // Birth up to 7 years
    4: banner09, // 8 to 14 years
    5: banner11, // 15 to 28 years
    6: banner04, // Stress Management
    7: banner07, // Health care
    8: banner05, // Meditation and yoga
    9: banner10, // Science and Spirituality
    10: banner06 // Life after death
  };

  return mapping[id] || null;
};


const EventHeroBanner = ({
  title,
  subtitle,
  reviewCount,
  topicLabel,
  topicId,
  topicColor,
  likes,
  dislikes,
  userReaction,
  onLike,
  onDislike,
  className = '',
  ...rest
}) => {
  // select a banner image based on topic id first (matches schedule cards),
  // then try label-based matching, then fallback to nothing
  const bannerImageById = getBannerForTopicId(topicId);
  const bannerImageByLabel = getBannerForTopic(topicLabel);
  const bannerImage = bannerImageById || bannerImageByLabel || null;

  // Preload the banner image to prevent flicker, but do not show any default image or background color
  const [loadedImage, setLoadedImage] = React.useState(null);
  React.useEffect(() => {
    if (!bannerImage) {
      setLoadedImage(null);
      return;
    }
    let didSet = false;
    const img = new window.Image();
    img.src = bannerImage;
    img.onload = () => {
      didSet = true;
      setLoadedImage(bannerImage);
    };
    img.onerror = () => {
      didSet = true;
      setLoadedImage(null);
    };
    // Fallback: if neither onload nor onerror fire in 2.5s, fallback to no image
    const timeout = setTimeout(() => {
      if (!didSet) setLoadedImage(null);
    }, 2500);
    return () => clearTimeout(timeout);
  }, [bannerImage]);
  // Build topic URL for navigation
  const topicUrl = topicLabel && topicId 
    ? `/topics/${encodeURIComponent(topicLabel)}/${topicId}`
    : null;

  return (
    <section
      className={`event-hero-banner ${className}`.trim()}
      style={{ position: 'relative' }}
      {...rest}
    >
      {loadedImage && (
        <img
          src={loadedImage}
          alt=""
          aria-hidden="true"
          className="event-hero-banner__img"
        />
      )}
      <div className="event-hero-banner__content">
        <h1 className="event-hero-banner__title">{title}</h1>
        {subtitle && <p className="event-hero-banner__subtitle">{subtitle}</p>}
        <div className="event-hero-banner__stats" style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
          {/* review */}
          <span 
            className="event-hero-banner__stat event-hero-banner__stat--clickable"
            onClick={() => {
              const writtenReviewsSection = document.getElementById('written-reviews-section');
              if (writtenReviewsSection) {
                writtenReviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {/* COLOR SQUARE */}
            <span
              className={`event-hero-banner__category-dot event-hero-banner__category-dot--${topicColor}`}
            />
            {reviewCount} Reviews
          </span>
          {/* 👍 LIKE */}
          <span
            className="event-hero-banner__stat"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
            onClick={onLike}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill={
                userReaction === 'like'
                  ? '#1e3a5f'
                  : '#ffffff'
              }
              stroke="#1e3a5f"
              strokeWidth="0.6"
            >
              <path d="M2 21h4V9H2v12zm20-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L13 1 6.59 7.41C6.22 7.78 6 8.3 6 8.83V19c0 1.1.9 2 2 2h9c.82 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-1z" />
            </svg>
            <span style={{ fontWeight: 600 }}>{likes}</span>
          </span>
          {/* 👎 DISLIKE */}
          <span
            className="event-hero-banner__stat"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
            onClick={onDislike}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill={
                userReaction === 'dislike'
                  ? '#1e3a5f'
                  : '#ffffff'
              }
              stroke="#1e3a5f"
              strokeWidth="0.8"
              style={{ 
                  marginTop: '5px',
                  marginLeft: '-10px',
              }}
            >
              <path d="M2 3h4v12H2V3zm20 11c0 1.1-.9 2-2 2h-6.31l.95 4.57.03.32c0 .41-.17-.79-.44-1.06L13 23l-6.41-6.41A2 2 0 0 1 6 15.17V5c0-1.1.9-2 2-2h9c.82 0 1.54.5 1.84 1.22l3.02 7.05c.09.23.14.47.14.73v1z"/>
            </svg>
            <span style={{ fontWeight: 600 }}>{dislikes}</span>
          </span>
          {/* Topic tag next to like/dislike */}
          {topicLabel && (
            topicUrl ? (
              <Link 
                to={topicUrl}
                className={`event-hero__topic event-hero__topic--${topicColor} event-hero__topic--clickable`}
                style={{ textDecoration: 'none', marginLeft: '12px', fontSize: '1rem', padding: '2px 12px', borderRadius: '16px', fontWeight: 500, background: 'rgba(255,255,255,0.85)' }}
              >
                {getShortTopicLabel(topicLabel)}
              </Link>
            ) : (
              <div className={`event-hero__topic event-hero__topic--${topicColor}`}
                style={{ marginLeft: '12px', fontSize: '1rem', padding: '2px 12px', borderRadius: '16px', fontWeight: 500, background: 'rgba(255,255,255,0.85)' }}
              >
                {getShortTopicLabel(topicLabel)}
              </div>
            )
          )}
        </div>
      </div>
      {/* <div className="event-hero-banner__decoration">
        <svg className="event-hero-banner__leaf-left" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path d="M40,100 Q60,40 100,60 T140,100" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none"/>
          <path d="M30,120 Q50,80 80,100 T120,140" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none"/>
        </svg>
        <svg className="event-hero-banner__woman" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
          <ellipse cx="150" cy="100" rx="60" ry="80" stroke="rgba(255,255,255,0.4)" strokeWidth="3" fill="none"/>
          <circle cx="150" cy="200" r="70" stroke="rgba(255,255,255,0.4)" strokeWidth="3" fill="none"/>
          <path d="M90,200 Q90,280 150,320 Q210,280 210,200" stroke="rgba(255,255,255,0.4)" strokeWidth="3" fill="none"/>
          <circle cx="135" cy="85" r="3" fill="rgba(255,255,255,0.5)"/>
          <circle cx="165" cy="85" r="3" fill="rgba(255,255,255,0.5)"/>
          <path d="M140,100 Q150,105 160,100" stroke="rgba(255,255,255,0.5)" strokeWidth="2" fill="none"/>
        </svg>
        <svg className="event-hero-banner__wave" viewBox="0 0 1200 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,50 Q300,20 600,50 T1200,50" stroke="rgba(255,255,255,0.2)" strokeWidth="2" fill="none"/>
          <path d="M0,70 Q300,40 600,70 T1200,70" stroke="rgba(255,255,255,0.15)" strokeWidth="2" fill="none"/>
        </svg>
      </div> */}
    </section>
  );
};

EventHeroBanner.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  reviewCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  topicLabel: PropTypes.string,
  topicId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  topicColor: PropTypes.string,

  likes: PropTypes.number.isRequired,
  dislikes: PropTypes.number.isRequired,
  userReaction: PropTypes.oneOf(['like', 'dislike', null]),
  onLike: PropTypes.func.isRequired,
  onDislike: PropTypes.func.isRequired,

  className: PropTypes.string,
};

EventHeroBanner.defaultProps = {
  subtitle: '',
  reviewCount: 0,
  topicLabel: '',
  topicId: '',
  topicColor: 'blue',
  likes: 0,
  dislikes: 0,
  userReaction: null,
  className: '',
};

export default EventHeroBanner;